import { ReactNode } from 'react';

import CurrencyAmount from '@components/currency-amount/CurrencyAmount';

interface PaymentSummaryStatsRowProps {
  totalPaid: number;
  totalDiscount: number;
  remainingBalance: number;
  isDefaultCurrency: boolean;
}

const amountClass = (isDefaultCurrency: boolean, color: string) =>
  `${
    isDefaultCurrency ? 'text-md !font-normal' : ''
  } text-sm font-semibold ${color}`;

const isNonZero = (n: number) => Math.abs(Number(n)) > 1e-9;

export const shouldShowPaymentSummaryStatsRow = (
  totalPaid: number,
  totalDiscount: number,
  remainingBalance: number
): boolean => {
  const hasDiscount = totalDiscount > 0;
  const paidAndRemainingDiffer = isNonZero(totalPaid - remainingBalance);
  const bothPaidAndRemainingNonZero =
    isNonZero(totalPaid) && isNonZero(remainingBalance);

  return hasDiscount || (paidAndRemainingDiffer && bothPaidAndRemainingNonZero);
};
const PaymentSummaryStatsRow = ({
  totalPaid,
  totalDiscount,
  remainingBalance,
  isDefaultCurrency
}: PaymentSummaryStatsRowProps) => {
  const gapClass = isDefaultCurrency ? 'gap-0.25' : 'gap-0.5';

  if (
    !shouldShowPaymentSummaryStatsRow(
      totalPaid,
      totalDiscount,
      remainingBalance
    )
  ) {
    return null;
  }

  const cells: { key: string; label: string; content: ReactNode }[] = [];

  cells.push({
    key: 'paid',
    label: 'Total paid',
    content: (
      <CurrencyAmount
        value={totalPaid}
        className={`justify-center ${gapClass}`}
        symbolClass={amountClass(isDefaultCurrency, 'text-cofeeGreen7')}
        valueClass={amountClass(isDefaultCurrency, 'text-cofeeGreen7')}
      />
    )
  });

  if (totalDiscount > 0) {
    cells.push({
      key: 'discount',
      label: 'Total discount',
      content: (
        <div className="flex items-baseline justify-center gap-0.5">
          <span className="text-xs font-semibold text-cofeeTeal300">-</span>
          <CurrencyAmount
            value={totalDiscount}
            className={`justify-center ${gapClass}`}
            symbolClass={amountClass(isDefaultCurrency, 'text-cofeeTeal300')}
            valueClass={amountClass(isDefaultCurrency, 'text-cofeeTeal300')}
          />
        </div>
      )
    });
  }

  cells.push({
    key: 'remaining',
    label: 'Remaining',
    content: (
      <CurrencyAmount
        value={remainingBalance}
        className={`justify-center ${gapClass}`}
        symbolClass={amountClass(isDefaultCurrency, 'text-cofeeOrange300')}
        valueClass={amountClass(isDefaultCurrency, 'text-cofeeOrange300')}
      />
    )
  });

  return (
    <div className="mt-4 w-full border-t border-cofeeGreyBlue200 pt-2">
      <div className="flex w-full divide-x divide-cofeeGreyBlue200 bg-white">
        {cells.map(({ key, label, content }) => (
          <div key={key} className="flex min-w-0 flex-1 flex-col items-center">
            <span className="text-center text-sm font-normal leading-tight text-cofeeNeutral8">
              {label}
            </span>
            {content}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentSummaryStatsRow;
